// src/features/test-runs/pages/TestRunsPage.tsx
// Test Runs management: list, create, edit, delete, start, complete, cancel, execute
// OpenAPI: GET/POST /api/testruns, GET/PUT/DELETE /api/testruns/{id},
//          POST /api/testruns/{id}/start, POST /api/testruns/{id}/complete,
//          POST /api/testruns/{id}/cancel, GET /api/testruns/{id}/progress,
//          POST /api/async/execute-tests (testcase-service)
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useState } from 'react'
import {
    Plus,
    Pencil,
    Trash2,
    Filter,
    X,
    Play,
    CheckCircle,
    XCircle,
    Clock,
    Zap,
} from 'lucide-react'
import {
    useTestRuns,
    useDeleteTestRun,
    useStartTestRun,
    useCompleteTestRun,
    useCancelTestRun,
} from '@/features/test-runs/api/useTestRuns'
import { useTestPlans } from '@/features/test-plans/api/useTestPlans'
import { TestRunDialog } from '@/features/test-runs/components/TestRunDialog'
import type { TestRun } from '@/types/testrun.types'
import type { TestPlan } from '@/types/testplan.types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/lib/api/client'
import { logger } from '@/lib/utils/logger'
import { toast } from 'sonner'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { formatDate } from '@/lib/utils'

const TestRunsPage = () => {
    const [selectedTestPlanId, setSelectedTestPlanId] = useState<string>('all')
    const [selectedStatus, setSelectedStatus] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [testRunDialogOpen, setTestRunDialogOpen] = useState(false)
    const [testRunDialogMode, setTestRunDialogMode] = useState<'create' | 'edit'>('create')
    const [selectedTestRun, setSelectedTestRun] = useState<TestRun | undefined>()
    const [deleteTestRunId, setDeleteTestRunId] = useState<number | null>(null)
    const [executingRunId, setExecutingRunId] = useState<number | null>(null)
    const [executeConfirm, setExecuteConfirm] = useState<{
        id: number
        name: string
    } | null>(null)

    const { data: testPlans } = useTestPlans()
    const { data: testRunsData, isLoading } = useTestRuns(
        selectedTestPlanId !== 'all' || selectedStatus !== 'all'
            ? {
                testplan_id:
                    selectedTestPlanId !== 'all'
                        ? parseInt(selectedTestPlanId, 10)
                        : undefined,
                status: selectedStatus !== 'all' ? selectedStatus : undefined,
            }
            : undefined
    )

    const deleteMutation = useDeleteTestRun()
    const startMutation = useStartTestRun()
    const completeMutation = useCompleteTestRun()
    const cancelMutation = useCancelTestRun()

    // useTestPlans returns TestPlan[] — normalize for safe access
    const testPlansList: TestPlan[] = Array.isArray(testPlans)
        ? testPlans
        : (testPlans as { testplans?: TestPlan[] } | undefined)?.testplans ?? []

    // Client-side search
    const filteredTestRuns = testRunsData?.test_runs?.filter((run) => {
        if (!searchQuery) return true
        const query = searchQuery.toLowerCase()
        return (
            run.name.toLowerCase().includes(query) ||
            run.notes?.toLowerCase().includes(query) ||
            run.testplan_name?.toLowerCase().includes(query)
        )
    })

    const activeFiltersCount = [
        selectedTestPlanId !== 'all',
        selectedStatus !== 'all',
        searchQuery.length > 0,
    ].filter(Boolean).length

    const handleClearFilters = () => {
        setSelectedTestPlanId('all')
        setSelectedStatus('all')
        setSearchQuery('')
    }

    const handleCreateTestRun = () => {
        setTestRunDialogMode('create')
        setSelectedTestRun(undefined)
        setTestRunDialogOpen(true)
    }

    const handleEditTestRun = (testRun: TestRun) => {
        setTestRunDialogMode('edit')
        setSelectedTestRun(testRun)
        setTestRunDialogOpen(true)
    }

    const handleDeleteTestRun = async () => {
        if (deleteTestRunId !== null) {
            try {
                await deleteMutation.mutateAsync(deleteTestRunId)
                toast.success('Test run deleted successfully')
            } catch (err) {
                logger.error('Failed to delete test run:', err)
                toast.error('Failed to delete test run')
            }
            setDeleteTestRunId(null)
        }
    }

    const handleStartTestRun = async (id: number) => {
        try {
            await startMutation.mutateAsync(id)
            toast.success('Test run started')
        } catch (err) {
            logger.error('Failed to start test run:', err)
            toast.error('Failed to start test run')
        }
    }

    const handleCompleteTestRun = async (id: number) => {
        try {
            await completeMutation.mutateAsync(id)
            toast.success('Test run completed')
        } catch (err) {
            logger.error('Failed to complete test run:', err)
            toast.error('Failed to complete test run')
        }
    }

    const handleCancelTestRun = async (id: number) => {
        try {
            await cancelMutation.mutateAsync(id)
            toast.success('Test run cancelled')
        } catch (err) {
            logger.error('Failed to cancel test run:', err)
            toast.error('Failed to cancel test run')
        }
    }

    const confirmExecuteTestRun = async () => {
        if (!executeConfirm) return

        try {
            setExecutingRunId(executeConfirm.id)

            await apiClient.post('/api/async/execute-tests', {
                test_run_id: executeConfirm.id,
                test_cases: [],
                parallel_execution: true,
                max_parallel: 10,
                timeout_seconds: 300,
            })

            toast.success(
                `Test run "${executeConfirm.name}" execution has been queued successfully!`
            )
        } catch (error) {
            logger.error('Failed to execute test run:', error)
            toast.error('Failed to start test run execution. Please try again.')
        } finally {
            setExecutingRunId(null)
            setExecuteConfirm(null)
        }
    }

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return (
                    <Badge className="bg-gray-100 text-gray-800">
                        <Clock className="mr-1 h-3 w-3" />
                        Pending
                    </Badge>
                )
            case 'running':
                return (
                    <Badge className="bg-blue-100 text-blue-800">
                        <Play className="mr-1 h-3 w-3" />
                        Running
                    </Badge>
                )
            case 'completed':
                return (
                    <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Completed
                    </Badge>
                )
            case 'cancelled':
                return (
                    <Badge className="bg-red-100 text-red-800">
                        <XCircle className="mr-1 h-3 w-3" />
                        Cancelled
                    </Badge>
                )
            default:
                return <Badge variant="secondary">{status}</Badge>
        }
    }

    return (
        <div className="p-8 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Test Runs</h1>
                    <p className="text-muted-foreground mt-1">
                        Manage automated test run sequences
                    </p>
                </div>
                <Button onClick={handleCreateTestRun}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Test Run
                </Button>
            </div>

            {/* Filters */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <Filter className="h-5 w-5" />
                            Filters
                            {activeFiltersCount > 0 && (
                                <Badge variant="secondary">{activeFiltersCount} active</Badge>
                            )}
                        </CardTitle>
                        {activeFiltersCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClearFilters}
                                className="h-8"
                            >
                                <X className="mr-2 h-4 w-4" />
                                Clear All
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Test Plan Filter */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Test Plan</label>
                            <Select
                                value={selectedTestPlanId}
                                onValueChange={setSelectedTestPlanId}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All test plans" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Test Plans</SelectItem>
                                    {testPlansList.map((tp: TestPlan) => (
                                        <SelectItem key={tp.id} value={tp.id.toString()}>
                                            {tp.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Status Filter */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Status</label>
                            <Select
                                value={selectedStatus}
                                onValueChange={setSelectedStatus}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="All statuses" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="running">Running</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Search */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Search</label>
                            <Input
                                placeholder="Search test runs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {activeFiltersCount > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2 border-t mt-4">
              <span className="text-sm text-muted-foreground">
                Active filters:
              </span>
                            {selectedTestPlanId !== 'all' && (
                                <Badge variant="secondary" className="gap-1">
                                    Test Plan:{' '}
                                    {testPlansList.find(
                                        (tp: TestPlan) => tp.id === parseInt(selectedTestPlanId, 10)
                                    )?.name || 'Unknown'}
                                    <X
                                        className="h-3 w-3 cursor-pointer"
                                        onClick={() => setSelectedTestPlanId('all')}
                                    />
                                </Badge>
                            )}
                            {selectedStatus !== 'all' && (
                                <Badge variant="secondary" className="gap-1">
                                    Status: {selectedStatus}
                                    <X
                                        className="h-3 w-3 cursor-pointer"
                                        onClick={() => setSelectedStatus('all')}
                                    />
                                </Badge>
                            )}
                            {searchQuery && (
                                <Badge variant="secondary" className="gap-1">
                                    Search: &quot;{searchQuery}&quot;
                                    <X
                                        className="h-3 w-3 cursor-pointer"
                                        onClick={() => setSearchQuery('')}
                                    />
                                </Badge>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Results */}
            <Card>
                <CardHeader>
                    <CardTitle>
                        {isLoading ? (
                            'Loading...'
                        ) : filteredTestRuns && filteredTestRuns.length > 0 ? (
                            <>
                                Showing {filteredTestRuns.length} of{' '}
                                {testRunsData?.test_runs?.length || 0} test runs
                                {activeFiltersCount > 0 && ' (filtered)'}
                            </>
                        ) : (
                            'No test runs found'
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                            Loading test runs...
                        </div>
                    ) : filteredTestRuns && filteredTestRuns.length > 0 ? (
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Test Plan</TableHead>
                                        <TableHead>Build</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Progress</TableHead>
                                        <TableHead>Pass Rate</TableHead>
                                        <TableHead>Started</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTestRuns.map((run: TestRun) => {
                                        const progressPercent =
                                            run.total_tests > 0
                                                ? Math.round(
                                                    (run.executed_tests / run.total_tests) * 100
                                                )
                                                : 0
                                        const passRate =
                                            run.executed_tests > 0
                                                ? Math.round(
                                                    (run.passed_tests / run.executed_tests) * 100
                                                )
                                                : 0

                                        return (
                                            <TableRow key={run.test_run_id}>
                                                <TableCell className="font-medium">
                                                    {run.name}
                                                </TableCell>
                                                <TableCell>{run.testplan_name || '-'}</TableCell>
                                                <TableCell>{run.build_name || '-'}</TableCell>
                                                <TableCell>{getStatusBadge(run.status)}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-blue-600"
                                                                style={{ width: `${progressPercent}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-xs text-muted-foreground">
                              {run.executed_tests}/{run.total_tests}
                            </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {run.executed_tests > 0 ? (
                                                        <span
                                                            className={
                                                                passRate >= 80
                                                                    ? 'text-green-600'
                                                                    : passRate >= 50
                                                                        ? 'text-yellow-600'
                                                                        : 'text-red-600'
                                                            }
                                                        >
                              {passRate}%
                            </span>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {run.started_at ? formatDate(run.started_at) : '-'}
                                                </TableCell>
                                                <TableCell className="text-right space-x-2">
                                                    {run.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    setExecuteConfirm({
                                                                        id: run.test_run_id,
                                                                        name: run.name,
                                                                    })
                                                                }
                                                                disabled={executingRunId === run.test_run_id}
                                                                title="Execute Automated Test"
                                                                className="text-green-600 hover:text-green-700"
                                                            >
                                                                <Zap className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleStartTestRun(run.test_run_id)
                                                                }
                                                                title="Start"
                                                            >
                                                                <Play className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                    {run.status === 'running' && (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleCompleteTestRun(run.test_run_id)
                                                                }
                                                                title="Complete"
                                                            >
                                                                <CheckCircle className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleCancelTestRun(run.test_run_id)
                                                                }
                                                                title="Cancel"
                                                            >
                                                                <XCircle className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                    {(run.status === 'pending' ||
                                                        run.status === 'running') && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditTestRun(run)}
                                                        >
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {(run.status === 'pending' ||
                                                        run.status === 'cancelled') && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() =>
                                                                setDeleteTestRunId(run.test_run_id)
                                                            }
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : activeFiltersCount > 0 ? (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground mb-4">
                                No test runs match your filters
                            </p>
                            <Button variant="outline" onClick={handleClearFilters}>
                                Clear Filters
                            </Button>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground mb-4">
                                No test runs created yet. Create your first test run to start
                                automated testing!
                            </p>
                            <Button onClick={handleCreateTestRun}>
                                <Plus className="mr-2 h-4 w-4" />
                                Create First Test Run
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Test Run Dialog */}
            <TestRunDialog
                open={testRunDialogOpen}
                onOpenChange={setTestRunDialogOpen}
                testRun={selectedTestRun}
                mode={testRunDialogMode}
            />

            {/* Delete Confirmation */}
            <AlertDialog
                open={deleteTestRunId !== null}
                onOpenChange={(open: boolean) => !open && setDeleteTestRunId(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Test Run?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this test run. This action cannot be
                            undone. Only pending and cancelled test runs can be deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteTestRun}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Execute Confirmation */}
            <AlertDialog
                open={executeConfirm !== null}
                onOpenChange={(open: boolean) => !open && setExecuteConfirm(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Execute Test Run?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Execute test run &quot;{executeConfirm?.name}&quot;? This will run
                            all test cases in the test plan using massively parallel
                            processing.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmExecuteTestRun}>
                            Execute
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

export default TestRunsPage